import { useMediaQuery } from '@mui/material';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { breakpoints, media } from 'src/assets/_responsive-mixin';
import { Icon } from 'src/components/shared/Icon/Icon';
import { push } from 'src/utils/navigation';
import styled from 'styled-components';
import { NavItems, NavItem, NavItemChild } from './nav-items';

type SidebarProps = {
  open: boolean;
  onToggleSidebar: () => void;
};

export const Sidebar = ({ open, onToggleSidebar }: SidebarProps) => {
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const [activeItem, setActiveItem] = useState<Nullable<NavItem>>(null);
  const [activeChild, setActiveChild] = useState<Nullable<NavItemChild>>(null);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const smallScreen = useMediaQuery(`(max-width: ${breakpoints.small})`);

  useEffect(() => {
    setNavItems(NavItems);
  }, []);

  const isNavItemActive = (navItem: NavItem, path: string) =>
    (navItem.route && path?.includes(navItem.route)) ||
    (navItem.children && navItem.children.some(c => path?.includes(c.route)));

  const initializeSidebar = useCallback(
    (path: string) => {
      navItems.forEach(item => {
        if (isNavItemActive(item, path)) {
          setActiveItem(item);
          setActiveChild(
            item.children?.find(c => path.includes(c.route)) ?? null
          );
        }
      });
    },
    [navItems]
  );

  useEffect(() => {
    if (!activeItem) {
      initializeSidebar(location.pathname);
    }
  }, [activeItem, location, initializeSidebar]);

  const onNavItemClick = (item: NavItem | NavItemChild) => {
    if ('icon' in item) setActiveItem(item);
    else setActiveChild(item as NavItemChild);

    if (item.route) {
      push(item.route, navigate);

      if (smallScreen) {
        onToggleSidebar();
      }
    }
  };

  return (
    <SidebarContainer className={open ? 'open' : ''}>
      <SidebarItems>
        {navItems
          .filter(item => !item.hidden)
          .map(item => (
            <Fragment key={item.name}>
              <SidebarItem
                className={activeItem?.name === item.name ? 'active' : ''}
                disabled={item.disabled}
                onClick={() => !item.disabled && onNavItemClick(item)}
              >
                <NavText>
                  <Icon className="mr-8" type={item.icon!} />
                  {t(`${item.name}`)}
                </NavText>
                {!!item.children && <Icon size={12} type="la-angle-down" />}
              </SidebarItem>
              {!!item.children && (
                <SidebarItemChildren
                  style={{
                    height:
                      activeItem?.name === item.name
                        ? item.children.length * 32
                        : 0
                  }}
                >
                  {item.children.map(child => (
                    <li
                      className={
                        activeChild?.name === child.name ? 'active' : ''
                      }
                      onClick={() => onNavItemClick(child)}
                      key={child.name}
                    >
                      {t(`${child.name}`)}
                    </li>
                  ))}
                </SidebarItemChildren>
              )}
            </Fragment>
          ))}
      </SidebarItems>
    </SidebarContainer>
  );
};

const SidebarContainer = styled.aside`
  position: fixed;
  left: 0;
  bottom: 0;
  width: var(--sidebar-width);
  height: calc(100% - var(--header-height));
  background: var(--sidebar-background);
  overflow: auto;
  z-index: 99;
  display: flex;
  flex-direction: column;
  padding: 2rem 0;

  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  transition: transform 225ms cubic-bezier(0, 0, 0.2, 1) 0ms;

  ${media.small`
    width: 260px;
    transform: translateX(-260px);
    
    &.open {
      transform: translateX(0px);
    }    
  `}

  ${media.xsmall`
    width: 100%;
    transform: translateX(-100%) !important;
    
    &.open {
      transform: translateX(0px) !important;
    }    
  `}
`;

const SidebarItems = styled.nav`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 100%;
`;

const SidebarItem = styled.span<{ disabled?: boolean }>`
  position: relative;
  height: 2.5rem;
  padding: 0 1.5rem;
  transition: all 200ms ease-in-out;

  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;

  opacity: ${props => (props.disabled ? 0.5 : 1)};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  color: var(--primary-color20);

  :before {
    content: '';
    position: absolute;
    left: 0;
    height: 0;
    width: 2px;
    transition: all 225ms ease-out;
  }

  &.active {
    color: #fff;

    :before {
      height: 100%;
      background: #db240b;
    }
  }

  :hover {
    color: #fff;
  }
`;

const SidebarItemChildren = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0 1.5rem;
  color: var(--primary-color20);
  transition: height 300ms ease-in-out;
  overflow: hidden;
  max-height: 100%;

  li {
    position: relative;
    cursor: pointer;
    height: 2rem;
    padding-left: 2rem;

    &.active,
    &:hover {
      color: #fff;
    }

    &:hover:before {
      transform: scaleX(3);
      background: #fff;
    }

    &.active:before {
      transform: scaleX(3);
      background: #db240b;
    }

    :before {
      content: '';
      position: absolute;
      display: flex;
      left: 10px;
      top: 12px;
      width: 6px;
      height: 1px;
      background: var(--primary-color20);
      transition: all 0.15s ease-out;
    }
  }
`;

const NavText = styled.span`
  display: flex;
  align-items: center;
  font-size: 1rem;
`;
